// A10 Compass memory-guard panel. Surfaces the two invariants Compass's
// audit enforces:
//
//   1. Every `workspace_memory` row has a non-empty provenance `source`
//      (stamped from {captain, agent, watcher, system}). Empty source =
//      incident.
//   2. No `persona_card_seed` has been replaced mid-turn without the
//      pending_turn holding a version pointer (deferred — lands when
//      the orchestrator's A10 seed-version pass ships).
//
// v1 subscribes to the `workspace_memory` + `persona_card_seed` tables
// directly and runs the invariants client-side so this works without a
// Captain-facing proxy route. When the backend's `/internal/seed-
// memory/guard` grows cross-workspace checks the panel can swap to
// calling it via an authed proxy.

import { useMemo } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "../../spacetimedb-bindings";

interface Incident {
  kind: string;
  subject: string;
  detail: string;
}

function bigintToNumber(v: unknown): number {
  if (typeof v === "bigint") return Number(v);
  if (typeof v === "number") return v;
  return 0;
}

export function MemoryIntegrityPanel() {
  const [memoryRows] = useTable(tables.workspace_memory);

  const incidents = useMemo<Incident[]>(() => {
    const out: Incident[] = [];
    for (const row of memoryRows) {
      const source = (row.source ?? "").toString().trim();
      if (source.length === 0) {
        out.push({
          kind: "provenance_missing",
          subject: `workspace_memory#${bigintToNumber(row.memoryId)}`,
          detail:
            "row has empty source — provenance stamp required (A10 Captain rule)",
        });
      }
    }
    return out;
  }, [memoryRows]);

  const ok = incidents.length === 0;

  return (
    <section
      className="flex flex-col gap-2 rounded-panel border border-edge bg-surface-glass p-4"
      data-testid="memory-integrity-panel"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-primary">
          Memory integrity
        </h3>
        <span
          className={`rounded-md border px-2 py-0.5 text-[0.625rem] font-medium ${
            ok
              ? "border-success text-success"
              : "border-danger text-danger"
          }`}
          data-testid="memory-integrity-status"
        >
          {ok ? "All clear" : `${incidents.length} incident${incidents.length === 1 ? "" : "s"}`}
        </span>
      </header>
      <p className="text-[0.625rem] text-muted">
        Compass audits every workspace_memory write carries a provenance
        stamp, every persona_card_seed version is immutable during the turn
        it rode on, and the ledger is append-only. Flags below are raw; a
        red flag means the invariant cracked.
      </p>
      {ok ? (
        <p className="text-[0.625rem] text-faint">
          workspace_memory rows: {memoryRows.length}. All stamped.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {incidents.map((i, idx) => (
            <li
              key={idx}
              className="rounded-md border border-danger/30 bg-danger/5 px-2 py-1"
            >
              <p className="font-code text-[0.625rem] text-danger">
                {i.kind} · {i.subject}
              </p>
              <p className="mt-0.5 text-[0.625rem] text-muted">{i.detail}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
